import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import * as d3 from "d3";
import "./chart.css";
import { MdLabel, MdLabelOff } from "react-icons/md";

const BarCharts = forwardRef(({ xData, yData }, ref) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const [chartWidth, setChartWidth] = useState(window.innerWidth * 0.9);
  const [chartHeight, setChartHeight] = useState(window.innerHeight * 0.7);
  const [visibleFields, setVisibleFields] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [legendSearch, setLegendSearch] = useState("");

  useImperativeHandle(ref, () => ({
    getUpdatedChartData: () => [...yData],
  }));

  useEffect(() => {
    const updateChartSize = () => {
      setChartWidth(window.innerWidth * 0.9);
      setChartHeight(window.innerHeight * 0.7);
    };
    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, []);

  useEffect(() => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0) return;
    // console.log("xData", xData);
    //     console.log("yData", yData);
    setVisibleFields(yData.map(d => d.field));
  }, [xData, yData]);

  useEffect(() => {
    if (!xData || !yData || xData.length === 0 || yData.length === 0) return;

    const margin = { top: 60, right: 20, bottom: 40, left: 60 };
    const height = chartHeight;
    const width = chartWidth;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .style("background-color", "#F2F2F2");

    const filteredYData = yData.filter(d => visibleFields.includes(d.field));
    const keys = filteredYData.map(d => d.field);

    const minY = d3.min(xData.map((_, i) =>
      d3.min(filteredYData.map(d => d.values[i] ?? 0))
    ));
    const maxY = d3.max(xData.map((_, i) =>
      d3.max(filteredYData.map(d => d.values[i] ?? 0))
    ));

    const xIndexScale = d3.scaleLinear()
      .domain([0, xData.length - 1])
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      .domain([minY * 1.1, maxY * 1.1])
      .range([height - margin.bottom, margin.top]);

    const xAxis = d3.axisBottom(xIndexScale)
      .ticks(Math.min(xData.length, 20))
      .tickFormat(i => xData[Math.round(i)] || "");

    const yAxis = d3.axisLeft(yScale);

    svg.append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(xAxis)
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    svg.append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(yAxis)
      .selectAll("text")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    svg.append("line")
      .attr("x1", margin.left)
      .attr("x2", width - margin.right)
      .attr("y1", yScale(0))
      .attr("y2", yScale(0))
      .attr("stroke", "black")
      .attr("stroke-width", 1.5);

    const tooltip = d3.select(tooltipRef.current)
      .style("opacity", 0)
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("background-color", "rgba(0,0,0,0.6)")
      .style("color", "white")
      .style("padding", "5px")
      .style("border-radius", "4px");

    const barArea = svg.append("g");

    const barGroups = barArea.selectAll(".bar-group")
      .data(xData.map((_, i) => i))
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("transform", d => `translate(${xIndexScale(d)},0)`);

    const defaultGroupWidth = (xIndexScale(1) - xIndexScale(0)) * 0.8;
    let x1 = d3.scaleBand().domain(keys).range([0, defaultGroupWidth]).padding(0.05);

    barGroups.selectAll("rect")
      .data(i => filteredYData.map(d => ({
        key: d.field,
        value: d.values[i] ?? 0,
        index: i,
        color: d.color
      })))
      .enter()
      .append("rect")
      .attr("x", d => x1(d.key))
      .attr("y", d => (d.value >= 0 ? yScale(d.value) : yScale(0)))
      .attr("width", x1.bandwidth())
      .attr("height", d => Math.abs(yScale(d.value) - yScale(0)))
      .attr("fill", d => d.color)
      .on("mouseover", (event, d) => {
        const [x, y] = d3.pointer(event, svg.node());
        tooltip.style("opacity", 1)
          .style("left", `${x + 10}px`)
          .style("top", `${y - 10}px`)
          .html(`
            <div style="color: white; font-size: 14px; font-weight: bold;">${xData[d.index]}</div>
            <div style="display: flex; align-items: center; margin-top: 2px;">
              <div style="width: 10px; height: 10px; background-color: ${d.color}; margin-right: 5px; border: 1px solid white;"></div>
              <span style="font-size: 13px; font-weight: bold;">${d.key}:</span>
              <span style="margin-left: 5px; font-size: 13px;">${d.value.toLocaleString()}</span>
            </div>
          `);
      })
      .on("mouseout", () => tooltip.style("opacity", 0));
const zoom = d3.zoom()
  .scaleExtent([1, 10])
  .translateExtent([
    [margin.left, margin.top], 
    [width - margin.right, height - margin.bottom]
  ])
  .extent([
    [margin.left, margin.top],
    [width - margin.right, height - margin.bottom]
  ])
  .on("zoom", (event) => {
    const newX = event.transform.rescaleX(xIndexScale);

    const zoomedGroupWidth = (newX(1) - newX(0)) * 0.8;
    const x1Zoom = d3.scaleBand()
      .domain(keys)
      .range([0, zoomedGroupWidth])
      .padding(0.05);

    svg.selectAll(".bar-group")
      .attr("transform", d => {
        let tx = newX(d);
        tx = Math.max(margin.left, Math.min(tx, width - margin.right - zoomedGroupWidth));
        return `translate(${tx},0)`;
      });

    const newXAxis = d3.axisBottom(newX)
      .ticks(Math.min(xData.length, 20))
      .tickFormat(i => xData[Math.round(i)] || "");

    svg.select(".x-axis")
      .call(newXAxis)
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    // Update bar widths
    svg.selectAll(".bar-group rect")
      .attr("x", d => x1Zoom(d.key))
      .attr("width", x1Zoom.bandwidth());
  });


    svg.call(zoom);

  }, [xData, yData, chartWidth, chartHeight, visibleFields]);

  return (
    <div className="chart-container" style={{ position: "relative", overflow: "visible" }}>
      <button
        onClick={() => setShowLegend(!showLegend)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          padding: "6px 12px",
          backgroundColor: "transparent",
          color: "black",
          borderRadius: "4px",
          cursor: "pointer"
        }}
      >
        {showLegend ? <MdLabelOff size={24} /> : <MdLabel size={24} />}
      </button>

      {showLegend && (
        <div
          className="legend-sidebar"
          style={{
            position: "absolute",
            top: 50,
            right: 10,
            width: "250px",
            maxHeight: "400px",
            backgroundColor: "#fff",
            boxShadow: "0 0 10px rgba(0,0,0,0.2)",
            padding: "10px",
            overflowY: "auto",
            zIndex: 9,
          }}
        >
          <input
            type="text"
            value={legendSearch}
            onChange={(e) => setLegendSearch(e.target.value)}
            placeholder="Search..."
            style={{
              width: "100%",
              padding: "6px 8px",
              marginBottom: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />

          <div>
            {yData
              .filter(d => d.values.some(v => v !== 0))
              .filter(d => d.field.toLowerCase().includes(legendSearch.toLowerCase()))
              .map(d => (
                <div
                  key={d.field}
                  onClick={() =>
                    setVisibleFields(prev =>
                      prev.includes(d.field)
                        ? prev.filter(f => f !== d.field)
                        : [...prev, d.field]
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      backgroundColor: d.color,
                      marginRight: "10px",
                    }}
                  />
                  <span
                    style={{
                      textDecoration: visibleFields.includes(d.field) ? "none" : "line-through",
                    }}
                  >
                    {d.field}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      <svg ref={svgRef}></svg>
      <div ref={tooltipRef}></div>
    </div>
  );
});

export default BarCharts;
